async function detectDeviceTier() {
  const mem = navigator.deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency ?? 4;

  // Try worker-based CPU bench first
  const benchTime = 10000; // fallback

  let score = 0;
  // Memory
  score += mem >= 8 ? 2 : mem >= 4 ? 1 : 0;
  // Cores
  score += cores >= 8 ? 2 : cores >= 4 ? 1 : 0;
  // CPU (lower is better)
  score += benchTime < 200 ? 2 : benchTime < 600 ? 1 : 0;

  const tier = score >= 5 ? "high" : score >= 3 ? "mid" : "low";

  return tier;
}

// function runCpuBenchInWorker({ iterations = 5e7, timeoutMs = 1200 } = {}) {

//   return new Promise((resolve, reject) => {
//     if (!window.Worker || !URL || !Blob) {
//       return reject(new Error("Workers not supported"));
//     }
//     const blob = new Blob(
//       [
//         `
//       self.onmessage = (e) => {
//         const { iterations } = e.data;
//         // Prevent dead-code elimination with a trivial accumulator
//         let acc = 0;
//         const start = performance.now();
//         for (let i = 0; i < iterations; i++) { acc += (i & 1); }
//         const dt = performance.now() - start;
//         // avoid optimizing acc away
//         if (acc === -1) console.log('noop');
//         self.postMessage({ dt });
//       };
//     `,
//       ],
//       { type: "application/javascript" },
//     );

//     const blobUrl = URL.createObjectURL(blob);
//     let worker;
//     try {
//       worker = new Worker(blobUrl);
//     } catch (err) {
//       URL.revokeObjectURL(blobUrl);
//       return reject(err);
//     }

//     let done = false;

//     const timer = setTimeout(() => {
//       if (done) return;
//       done = true;
//       try {
//         worker.terminate();
//       } catch (e) {}
//       URL.revokeObjectURL(blobUrl);
//       reject(new Error("Benchmark timed out"));
//     }, timeoutMs);

//     worker.onmessage = (ev) => {
//       if (done) return;
//       done = true;
//       clearTimeout(timer);
//       try {
//         worker.terminate();
//       } catch (e) {}
//       URL.revokeObjectURL(blobUrl);
//       resolve(ev.data.dt);
//     };
//     worker.onerror = (err) => {
//       if (done) return;
//       done = true;
//       clearTimeout(timer);
//       try {
//         worker.terminate();
//       } catch (e) {}
//       URL.revokeObjectURL(blobUrl);
//       reject(err);
//     };
//     try {
//       worker.postMessage({ iterations });
//     } catch (err) {
//       clearTimeout(timer);
//       try {
//         worker.terminate();
//       } catch (e) {}
//       URL.revokeObjectURL(blobUrl);
//       reject(err);
//     }
//   });
// }

/**
 * VideoScrollPlayer Custom HTML Element (Hybrid Playback)
 *
 * This custom element provides highly smooth scroll-based video playback,
 * intelligently choosing between WebCodecs (for precise frame control)
 * and native HTML <video> element control (using playbackRate and currentTime).
 * It uses MP4Box.js for WebCodecs path to demux MP4 files.
 *
 * Features:
 * - Hybrid Playback: Prioritizes WebCodecs for frame-accurate scrubbing,
 * falls back to native <video> control if WebCodecs is disabled or unsupported.
 * - Smooth Animation: Utilizes requestAnimationFrame with time-based easing
 * for seamless transitions between target video times/frames.
 * - Responsive Video: Maintains aspect ratio and "cover" behavior.
 * - Custom Scroll Boundaries: Defines video playback start/end using other elements.
 * - Loading Indicator: Provides feedback during video loading and decoding.
 * - `start-offset-percent`: Starts video playback from a percentage point.
 * - `loop-video`: Enables seamless looping of the video/frames.
 * - `use-webcodecs`: Boolean attribute to explicitly enable/disable WebCodecs attempt.
 *
 * Usage:
 * <video-scroll-player
 * [scroll-target="#your-scroll-container-id"]
 * [start-offset-percent="0"]
 * [loop-video]
 * [scroll-start-element="#section-start"]
 * [scroll-end-element="#section-end"]
 * [use-webcodecs]
 * >
 * <video muted playsinline> <!-- playsinline is important for mobile autoplay -->
 * <source src="path/to/your-video.mp4" type="video/mp4">
 * <source src="path/to/your-video.webm" type="video/webm">
 * </video>
 * </video-scroll-player>
 *
 * IMPORTANT: If `use-webcodecs` is enabled, you must include MP4Box.js script:
 * <script src="https://cdn.jsdelivr.net/npm/mp4box@0.6.2/dist/mp4box.all.min.js" type="module"></script>
 */

// Import MP4Box from its module source
// This import is conditional on whether MP4Box.js is actually used at runtime.
// If use-webcodecs is false or WebCodecs not supported, this import might still happen
// but its functions won't be called.

class VideoScrollPlayer extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    // Core elements
    this.videoElement = null;
    this.canvas = null;
    this.context = null;
    this.frames = [];
    this.frameRate = 0;
    this.currentTime = 0;
    this.targetTime = 0;
    this.transitioningRaf = null;
    this.transitionSpeed = 8;
    this.frameThreshold = 0.1;
    this.useWebCodecs = false;
    this.isLoaded = false;
    this.videoPercentage = 0;
    this.lastScrollTop = 0;
    this.scrollTimeout = null;

    this.maxConcurrency = parseInt(
      this.getAttribute("max-concurrency") || 8,
      10,
    );
    this.maxCachedFrames = parseInt(
      this.getAttribute("max-cached-frames") || 120,
      10,
    );
    this.dprCap = parseFloat(this.getAttribute("dpr-cap") || 1.5);
    this._frameCache = new Map(); // index -> ImageBitmap
    this._inflight = new Map(); // index -> Promise
    this._aborters = new Map(); // index -> AbortController
    this._paintScheduled = false;
    this._pendingFrameIndex = 0;

    this.isMobileSafari = false;
    this.isSafari = false;
    this.resizeObserver = null;

    this.imageSequencePrefix = null;
    this.imageSequenceCount = 0;
    this.imageSequenceLoaded = false;

    this._onScroll = this._onScroll.bind(this);
    // Shadow DOM structure
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; width: 100%; height: 100%; position: relative; overflow: hidden; }
        #video-wrapper { scale:1.1; position: relative; height: 100%; width: 100%; display: flex; justify-content: center; align-items: center; overflow: hidden; }
        video, canvas { width: 100%; height: 100%; object-fit: cover; display: block;, image-rendering: pixelated; }
        video { opacity: 0; pointer-events: none; position: absolute; }
        canvas { opacity: 0;  display: none; }
        .loader { display:none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-weight: bold; z-index: 10; background: rgba(0,0,0,0.7); padding: 12px 25px; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); transition: opacity 0.3s; font-size: 1.1em; }
        .loader.hidden { opacity: 0; pointer-events: none; }
        @media screen and (max-width: 749px) {
            #video-wrapper { width: 100%; height: 100%; top: 0; left: 0; transform: none; }
            canvas { scale: 1;}
        }
      </style>
      <div id="video-wrapper">
        <slot name="video-slot"></slot>
        <canvas style="opacity: 0;"></canvas>
        <div class="loader">Loading...</div>
      </div>
    `;
    this.canvas = this.shadowRoot.querySelector("canvas");
    this.context = this.canvas.getContext("2d", {
      alpha: true,
      desynchronized: true,
    });
    this.videoWrapper = this.shadowRoot.querySelector("#video-wrapper");
    this.loader = this.shadowRoot.querySelector(".loader");

    this._setScrollContainer();
  }

  connectedCallback() {
    // Detect Instagram WebView - critical for preventing blank pages
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isInstagram = /instagram/i.test(ua) || /FBAN|FBAV/i.test(ua);
    const isWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) || /wv|Version\/[\d\.]+.*Chrome\/[\d\.]+ Mobile/i.test(ua);
    const isInstagramIAB = isInstagram || isWebView;

    detectDeviceTier().then((tier) => {
      // Don't skip initialization in Instagram IAB - it needs to work even on low-tier detection
      if (this.deviceTier == "low" && !isInstagramIAB) return;

      this.deviceTier = tier;

      this.isMobileSafari =
        /iP(ad|hone|od)/.test(navigator.userAgent) &&
        /WebKit/.test(navigator.userAgent) &&
        !/CriOS|FxiOS|OPiOS|mercury|EdgiOS/.test(navigator.userAgent);

      this.isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      );

      this.isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);

      this.videoElement =
        this.querySelector("video") ||
        this.shadowRoot.querySelector('slot[name="video-slot"]');
      if (!this.videoElement) {
        this.loader.textContent = "Error: No video element found!";
        this.loader.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
        return;
      }
      this.videoElement.muted = true;
      this.videoElement.loop = false;
      this.videoElement.preload = "auto";
      this.videoElement.playsInline = true;
      let videoSrc = "";
      const sources = this.videoElement.querySelectorAll("source");
      if (sources.length > 0) {
        for (const source of sources) {
          if (source.type === "video/mp4" || source.src.endsWith(".mp4")) {
            videoSrc = source.src;
            break;
          }
        }
        if (!videoSrc && sources[0].src) videoSrc = sources[0].src;
      } else if (this.videoElement.src) {
        videoSrc = this.videoElement.src;
      }
      if (!videoSrc) {
        this.loader.textContent = "Error: No video source found!";
        this.loader.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
        return;
      }
      this.useWebCodecs =
        this.isMobileSafari || this.isMobile
          ? false
          : this.hasAttribute("use-webcodecs");
      // Read attributes
      this._updateAttributes();
      this.lastScrollTop =
        this.scrollContainer.scrollTop || this.scrollContainer.scrollY || 0;

      this.videoElement.style.opacity = "1";
      this.canvas.style.display = "none";
      this.loader.classList.add("hidden");
      this.isLoaded = true; // Assume loaded if not using WebCodecs
      // Set isLoaded to true when native video is used and can play through
      this.videoElement.load();
      this.videoElement.pause();
      this.videoElement.currentTime = 0; // Reset to start
      this.videoElement.addEventListener(
        "canplaythrough",
        () => {
          this.isLoaded = true;
          this.videoElement.pause();
        },
        { once: true },
      );

      this.resizeObserver = new ResizeObserver(() => this._resizeCanvas());
      this.resizeObserver.observe(this.videoWrapper);

      // Instagram IAB Fallback: If video/canvas not loaded after 3 seconds, force visible
      // This prevents blank/transparent pages in Instagram WebView
      if (isInstagramIAB) {
        setTimeout(() => {
          if (!this.isLoaded) {
            console.warn('[VideoScrollPlayer] Instagram IAB: Forcing canvas visible after timeout');
            // Force canvas visible with fallback state
            if (this.canvas) {
              this.canvas.style.opacity = "1";
              this.canvas.style.display = "block";
            }
            if (this.videoElement) {
              this.videoElement.style.opacity = "1";
            }
            this.loader.classList.add("hidden");
            this.isLoaded = true; // Mark as loaded to prevent re-triggering
          }
        }, 3000);
      }
    });
  }
  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollContainer)
      this.scrollContainer.removeEventListener("scroll", this._onScroll);
    if (this._io) this._io.disconnect();
    if (this._prefetchRaf)
      cancelIdleCallback
        ? cancelIdleCallback(this._prefetchRaf)
        : clearTimeout(this._prefetchRaf);
    for (const [, ac] of this._aborters) ac.abort();
    for (const [, bmp] of this._frameCache) {
      try {
        bmp.close && bmp.close();
      } catch {}
    }
    this._frameCache.clear();
    this._inflight.clear();
    this._aborters.clear();

    // Clear canvas and context
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * _setScrollContainer: Determines which element will be used as the scroll listener.
   */
  _setScrollContainer(selector = null) {
    let newScrollContainer = window; // Default

    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        newScrollContainer = element;
      } else {
        console.warn(
          `VideoScrollPlayer: Scroll target element with selector "${selector}" not found. Falling back to document scroll.`,
        );
      }
    }

    // Only update if the scroll container has actually changed
    if (
      this._oldScrollContainer &&
      this._oldScrollContainer !== newScrollContainer
    ) {
      this._oldScrollContainer.removeEventListener("scroll", this._onScroll);
    }

    this.scrollContainer = newScrollContainer;
    this.scrollContainer.addEventListener("scroll", this._onScroll, {
      passive: true,
    });
    this._oldScrollContainer = this.scrollContainer;
  }

  async _loadImageSequence() {
    this.loader.textContent = "Loading images...";
    this.frames = [];
    const loadPromises = [];
    for (let i = 0; i <= this.imageSequenceCount; i++) {
      const padded = String(i).padStart(2, "0");
      const url = `https://cdn.shopify.com/s/files/1/0756/2398/9565/files/${this.imageSequencePrefix}${padded}.jpg`;
      loadPromises.push(
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null); // skip failed images
          img.src = url;
        }),
      );
    }
    const images = await Promise.all(loadPromises);
    this.frames = images.filter(Boolean);
    if (this.frames.length === 0) {
      this.loader.textContent = "No images loaded.";
      this.loader.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
      return;
    }
    this.frameRate = 30; // Assume 30fps for image sequence
    this.videoElement && (this.videoElement.style.display = "none");
    this.canvas.style.opacity = "1";
    this.loader.classList.add("hidden");
    this.canvas.style.display = "block";
    this.paintCanvasFrame(0);
    this.isLoaded = true;
    this.imageSequenceLoaded = true;
    this.scrollContainer.addEventListener("scroll", this._onScroll, {
      passive: true,
    });
  }

  async _loadImageSequenceWindowed() {
    this.loader.textContent = "Loading images...";
    this.frames = []; // will become sparse; we use _frameCache instead
    this.imageSequenceLoaded = true;

    const total = this.imageSequenceCount + 1;
    const pad = (i) => String(i).padStart(2, "0");

    // Compute target pixel width (we draw “cover” by height)
    const container = this.videoWrapper || this;
    const dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
    const targetH = Math.min(
      1600,
      Math.max(1, Math.round(container.offsetHeight * dpr * 2)),
    );
    console.l;
    // Optional: request modern formats if your CDN supports it
    const urlFor = (i) => {
      const base = `https://cdn.shopify.com/s/files/1/0756/2398/9565/files/${this.imageSequencePrefix}${pad(i)}.jpg`;
      // Shopify supports width transforms on files; fall back gracefully if not.
      return `${base}?height=${targetH}`;
    };

    const limit = this.maxConcurrency;
    const around = 40; // keep ±N frames hot

    const loadBitmap = async (idx) => {
      if (this._frameCache.has(idx)) return this._frameCache.get(idx);
      if (this._inflight.has(idx)) return this._inflight.get(idx);

      const ac = new AbortController();
      this._aborters.set(idx, ac);

      const p = (async () => {
        try {
          const res = await fetch(urlFor(idx), {
            signal: ac.signal,
            mode: "cors",
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const bmp = await createImageBitmap(blob, {
            resizeQuality: "pixelated",
          });
          this._frameCache.set(idx, bmp);
          return bmp;
        } finally {
          this._inflight.delete(idx);
          this._aborters.delete(idx);
        }
      })();

      this._inflight.set(idx, p);
      return p;
    };

    // Simple concurrency gate
    const pool = new Set();
    const run = async (task) => {
      while (pool.size >= limit) {
        await Promise.race(pool);
      }
      const pr = task().finally(() => pool.delete(pr));
      pool.add(pr);
      return pr;
    };

    // --- OPTIMIZATION: Load middle frame first ---
    const middleFrameIndex = Math.floor(total / 2);
    this._pendingFrameIndex = middleFrameIndex; // Set initial frame

    try {
      await loadBitmap(middleFrameIndex);

      // First, paint the canvas while it's still hidden
      this.paintCanvasFrame(middleFrameIndex);
      this.setAttribute("data-loaded", "true");

      // Then, make it visible. This should prevent the flash.
      this.videoElement && (this.videoElement.style.display = "none");
      this.canvas.style.display = "block";
      this.canvas.style.opacity = "1";
      this.loader.classList.add("hidden");

      this.isLoaded = true;
      this.frameRate = 60; // or compute if you know it
    } catch (e) {
      console.error(
        `Failed to load initial middle frame ${middleFrameIndex}`,
        e,
      );
      this.loader.textContent = "Error loading image.";
      this.loader.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
    }

    // --- Start background loading ---
    const initialBurstCount = Math.min(60, total);
    const initialLoads = [];
    for (let i = 0; i < initialBurstCount; i++) {
      if (i !== middleFrameIndex) {
        // Don't reload middle frame
        initialLoads.push(run(() => loadBitmap(i).catch((e) => {})));
      }
    }
    // Don't await the burst, let it happen in the background.

    // Start prefetch loop (keeps a ring buffer around current frame)
    const prefetch = async () => {
      if (!this.imageSequenceLoaded) return;
      const idx = this._pendingFrameIndex || 0;
      const start = Math.max(0, idx - around);
      const end = Math.min(total - 1, idx + around);

      // queue nearby frames
      for (let i = start; i <= end; i++) {
        if (!this._frameCache.has(i) && !this._inflight.has(i)) {
          run(() => loadBitmap(i).catch((e) => {}));
        }
      }

      // evict far frames to cap memory
      if (this._frameCache.size > this.maxCachedFrames) {
        const keys = Array.from(this._frameCache.keys());
        for (const key of keys) {
          if (this._frameCache.size <= this.maxCachedFrames) break;
          if (key < start - 10 || key > end + 10) {
            const bmp = this._frameCache.get(key);
            if (bmp) {
              try {
                bmp.close && bmp.close();
              } catch {}
            }
            this._frameCache.delete(key);
            const aborter = this._aborters.get(key);
            if (aborter) {
              aborter.abort();
              this._aborters.delete(key);
            }
          }
        }
      }

      // schedule next sweep
      this._prefetchRaf = requestIdleCallback
        ? requestIdleCallback(prefetch, { timeout: 500 })
        : setTimeout(prefetch, 250);
    };
    prefetch();
  }

  _resizeCanvas() {
    if (!this.canvas) return;
    const container = this.videoWrapper || this.canvas.parentElement || this;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    if (
      this.canvas.width !== containerWidth * 2 ||
      this.canvas.height !== containerHeight * 2
    ) {
      this.canvas.width = containerWidth * 2;
      this.canvas.height = containerHeight * 2;
      this.canvas.style.width = `${containerWidth}px`;
      this.canvas.style.height = `${containerHeight}px`;

      // Repaint the current frame after resize
      if (this.useWebCodecs && this.frames.length > 0) {
        const frameIndex = Math.floor(this.currentTime * this.frameRate);
        this.paintCanvasFrame(frameIndex);
      }
    }
  }

  setVideoPercentage(percentage, options = {}) {
    if (!this.isLoaded) return;
    if (this.videoPercentage === percentage) return;
    if (this.transitioningRaf) cancelAnimationFrame(this.transitioningRaf);
    this.videoPercentage = percentage;
    this.setTargetTimePercent(percentage, options);
  }

  setTargetTimePercent(percentage, options = {}) {
    const targetDuration =
      this.frames.length && this.frameRate
        ? this.frames.length / this.frameRate
        : this.videoElement.duration;
    this.targetTime = Math.max(Math.min(percentage, 1), 0) * targetDuration;
    if (
      !options.jump &&
      Math.abs(this.currentTime - this.targetTime) < this.frameThreshold
    )
      return;
    this.transitionToTargetTime(options);
  }

  transitionToTargetTime({ jump, transitionSpeed = this.transitionSpeed }) {
    const diff = this.targetTime - this.currentTime;
    const distance = Math.abs(diff);
    const duration = distance * 1000;
    const isForwardTransition = diff > 0;
    const tick = ({ startCurrentTime, startTimestamp, timestamp }) => {
      const progress = (timestamp - startTimestamp) / duration;
      const hasPassedThreshold = isForwardTransition
        ? this.currentTime >= this.targetTime
        : this.currentTime <= this.targetTime;
      if (
        isNaN(this.targetTime) ||
        Math.abs(this.targetTime - this.currentTime) < this.frameThreshold ||
        hasPassedThreshold
      ) {
        if (this.transitioningRaf) {
          cancelAnimationFrame(this.transitioningRaf);
          this.transitioningRaf = null;
        }
        return;
      }
      if (this.targetTime > this.videoElement.duration)
        this.targetTime = this.videoElement.duration;
      if (this.targetTime < 0) this.targetTime = 0;
      const transitionForward = this.targetTime - this.currentTime;
      if (this.canvas) {
        if (jump) {
          this.currentTime = this.targetTime;
        } else {
          this.currentTime += transitionForward / (256 / transitionSpeed);
        }
        this.paintCanvasFrame(Math.floor(this.currentTime * this.frameRate));
      } else {
        this.videoElement.currentTime = this.currentTime;
      }
      if (typeof requestAnimationFrame === "function") {
        this.transitioningRaf = requestAnimationFrame((currentTimestamp) =>
          tick({
            startCurrentTime,
            startTimestamp,
            timestamp: currentTimestamp,
          }),
        );
      }
    };
    if (typeof requestAnimationFrame === "function") {
      this.transitioningRaf = requestAnimationFrame((startTimestamp) => {
        tick({
          startCurrentTime: this.currentTime,
          startTimestamp,
          timestamp: startTimestamp,
        });
      });
    }
  }

  paintCanvasFrame(frameNum) {
    const bmp = this._frameCache.get(frameNum);
    if (!bmp) return;

    if (this._paintScheduled) {
      this._pendingFrameIndex = frameNum;
      return;
    }
    this._paintScheduled = true;
    this._pendingFrameIndex = frameNum;

    requestAnimationFrame(() => {
      const currFrame = this._frameCache.get(this._pendingFrameIndex);
      if (!currFrame) {
        this._paintScheduled = false;
        return;
      }

      const container = this.videoWrapper || this.canvas.parentElement || this;
      const cw = this.canvas.width,
        ch = this.canvas.height;
      const frameAspect = currFrame.width / currFrame.height;
      const drawH = ch;
      const drawW = Math.round(drawH * frameAspect);
      const offsetX = Math.round((cw - drawW) / 2);

      this.context.clearRect(0, 0, cw, ch);
      this.context.drawImage(
        currFrame,
        0,
        0,
        currFrame.width,
        currFrame.height,
        offsetX,
        0,
        drawW,
        drawH,
      );
      this._paintScheduled = false;
    });
  }
  /**
   * _updateAttributes: Reads and parses the custom element's attributes.
   */
  _updateAttributes() {
    // Video options
    const offsetAttr = this.getAttribute("start-offset-percent");
    this.startOffsetPercent = Math.max(
      0,
      Math.min(100, parseFloat(offsetAttr) || 0),
    );
    this.loopVideo = this.hasAttribute("loop-video");
    this.usePlaybackRate = this.hasAttribute("use-playback-rate");

    // Scroll boundary elements
    const scrollStartSelector = this.getAttribute("scroll-start-element");
    this.scrollStartElement = scrollStartSelector
      ? document.querySelector(scrollStartSelector)
      : null;
    if (scrollStartSelector && !this.scrollStartElement) {
      console.warn(
        `VideoScrollPlayer: 'scroll-start-element' with selector "${scrollStartSelector}" not found.`,
      );
    }

    const scrollEndSelector = this.getAttribute("scroll-end-element");
    this.scrollEndElement = scrollEndSelector
      ? document.querySelector(scrollEndSelector)
      : null;
    if (scrollEndSelector && !this.scrollEndElement) {
      console.warn(
        `VideoScrollPlayer: 'scroll-end-element' with selector "${scrollEndSelector}" not found.`,
      );
    }
  }

  /**
   * Image sequence support: image-sequence-prefix and image-sequence-count attributes.
   */
  static get observedAttributes() {
    return [
      "scroll-target",
      "start-offset-percent",
      "loop-video",
      "scroll-start-element",
      "scroll-end-element",
      "use-webcodecs",
      "use-playback-rate",
      "image-sequence-prefix",
      "image-sequence-count",
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (["image-sequence-prefix", "image-sequence-count"].includes(name)) {
      this.imageSequencePrefix = this.getAttribute("image-sequence-prefix");
      this.imageSequenceCount =
        parseInt(this.getAttribute("image-sequence-count"), 10) || 0;
      if (this.imageSequencePrefix && this.imageSequenceCount > 0) {
        this._loadImageSequenceWindowed();
      }
      return;
    }
    if (name === "scroll-target" && oldValue !== newValue) {
      this._setScrollContainer(newValue);
    } else if (
      [
        "start-offset-percent",
        "loop-video",
        "scroll-start-element",
        "scroll-end-element",
        "use-webcodecs",
        "use-playback-rate",
      ].includes(name)
    ) {
      // Re-initialize based on new attributes for use-webcodecs,
      // or re-evaluate scroll for other changes.
      if (name === "use-webcodecs") {
        // Changing use-webcodecs dynamically is complex as it requires re-loading/decoding.
        // For simplicity, this is not fully supported for dynamic switching after init.
        // It's primarily for initial setup.
        console.warn(
          "Changing 'use-webcodecs' attribute after initialization is not fully supported and may require re-loading the page.",
        );
        this.useWebCodecs =
          this.isMobileSafari || this.isMobile
            ? false
            : this.hasAttribute("use-webcodecs"); // Update the property
        // A full re-init might be needed here for production, but complex.
      } else {
        this._updateAttributes();
        if (this.isLoaded) {
          this._onScroll();
        }
      }
    }
  }

  _onScroll() {
    if (this.imageSequenceLoaded) {
      // Progress independent of scroll container:
      const rect = this.getBoundingClientRect();
      // Use visualViewport API for Instagram IAB compatibility
      const vh =
        this.scrollContainer && this.scrollContainer !== window
          ? this.scrollContainer.clientHeight
          : (window.visualViewport ? window.visualViewport.height : window.innerHeight) || 600;

      // 0 when viewport bottom touches element top,
      // 1 when viewport top touches element bottom
      const totalSpan = rect.height + vh;
      const raw = totalSpan > 0 ? (vh - rect.top) / totalSpan : 0;
      const p = Math.max(0, Math.min(1, raw));

      // Be robust if using a cache/windowed loader:
      const totalFrames =
        this.frames && this.frames.length > 1
          ? this.frames.length
          : (this.imageSequenceCount | 0) + 1; // fallback

      if (totalFrames <= 1) {
        // Nothing (or only one frame) to scrub
        // console.warn('No/one frame available', { totalFrames, framesLen: this.frames?.length });
        return;
      }

      const startFrame = Math.floor(
        (this.startOffsetPercent / 100) * (totalFrames - 1),
      );
      const endFrame = totalFrames - 1;

      let idx = Math.round(startFrame + p * (endFrame - startFrame));
      idx = Math.max(startFrame, Math.min(endFrame, idx));

      // Coalesce repaints
      if (idx !== this._lastDrawnIndex) {
        this._lastDrawnIndex = idx;
        this._pendingFrameIndex = idx;
        this.paintCanvasFrame(idx);
      }
      return;
    }
    // Use visualViewport API for Instagram IAB compatibility
    const viewportHeight = window.visualViewport ? window.visualViewport.height :
                          window.innerHeight || document.documentElement.clientHeight || 600;
    const currentScrollTop =
      this.scrollContainer.scrollTop || this.scrollContainer.scrollY;
    const scrollDirection =
      currentScrollTop > this.lastScrollTop ? "down" : "up";
    this.lastScrollTop = currentScrollTop;

    let scrollStart = 0;
    let scrollEnd = 0;

    // Determine scroll boundaries based on attributes or fall back to self boundaries
    if (this.scrollStartElement && this.scrollEndElement) {
      const startRect = this.scrollStartElement.getBoundingClientRect();
      const endRect = this.scrollEndElement.getBoundingClientRect();
      scrollStart = startRect.top + currentScrollTop - viewportHeight;
      scrollEnd = endRect.top + currentScrollTop;
      if (scrollStart >= scrollEnd) {
        const elementRect = this.getBoundingClientRect();
        const elementTopRelativeToScrollContainer =
          elementRect.top + currentScrollTop;
        const elementHeight = this.offsetHeight;
        scrollStart = elementTopRelativeToScrollContainer - viewportHeight;
        scrollEnd = elementTopRelativeToScrollContainer + elementHeight;
      }
    } else {
      const elementRect = this.getBoundingClientRect();
      const elementTopRelativeToScrollContainer =
        elementRect.top + currentScrollTop;
      const elementHeight = this.offsetHeight;
      scrollStart = elementTopRelativeToScrollContainer - viewportHeight;
      scrollEnd = elementTopRelativeToScrollContainer + elementHeight;
    }

    const effectiveScrollRange = scrollEnd - scrollStart;
    let rawScrollProgress = 0;
    if (effectiveScrollRange > 0) {
      rawScrollProgress =
        (currentScrollTop - scrollStart) / effectiveScrollRange;
    }
    // Clamp progress between 0 and 1
    const clampedScrollProgress = Math.max(0, Math.min(1, rawScrollProgress));
    const startFrame = Math.floor(
      (this.startOffsetPercent / 100) * (this.frames.length - 1),
    );
    let frameIndex;
    let targetTime;
    if (this.hasAttribute("loop-video")) {
      // Loop from startFrame to the frame before startFrame (wrap around)
      if (this.useWebCodecs) {
        const framesInLoop = this.frames.length;
        frameIndex =
          (startFrame + Math.floor(clampedScrollProgress * framesInLoop)) %
          this.frames.length;
        targetTime = frameIndex / this.frameRate;
      } else {
        // For native video, we can use the currentTime directly
        targetTime =
          (this.videoElement.duration * clampedScrollProgress +
            (this.startOffsetPercent / 100) * this.videoElement.duration) %
          this.videoElement.duration;
      }
    } else {
      const endFrame = this.frames.length - 1;
      frameIndex = Math.floor(
        startFrame + clampedScrollProgress * (endFrame - startFrame),
      );
      frameIndex = Math.max(startFrame, Math.min(endFrame, frameIndex));
      targetTime = frameIndex / this.frameRate;
    }

    if (
      (!this.useWebCodecs || this.isMobileSafari || this.isMobile) &&
      !this.imageSequenceLoaded
    ) {
      if (this.videoElement) {
        if (this.usePlaybackRate && !this.isMobileSafari) {
          if (scrollDirection === "down" || this.isSafari) {
            const timeDiff = targetTime - this.videoElement.currentTime;

            if (timeDiff < 0 && !this.isSafari) {
              this.videoElement.pause();
              this.videoElement.currentTime = targetTime;
              return;
            }

            this.videoElement.play();
            const rate = timeDiff * 4;
            this.videoElement.playbackRate = Math.max(0, Math.min(16, rate));

            clearTimeout(this.scrollTimeout);
            const timeToCatchUp =
              timeDiff > 0 && this.videoElement.playbackRate > 0
                ? (timeDiff / this.videoElement.playbackRate) * 1000
                : 150; // fallback timeout
            this.scrollTimeout = setTimeout(
              () => {
                if (this.videoElement) {
                  this.videoElement.playbackRate = 0;
                }
              },
              Math.min(500, timeToCatchUp + 50),
            ); // Add a small buffer and cap it
          } else {
            this.videoElement.pause();
            this.videoElement.currentTime = targetTime;
          }
        } else {
          this.videoElement.pause();
          this.videoElement.currentTime = targetTime;
        }
      }
    } else {
      // Use canvas painting for WebCodecs
      this.paintCanvasFrame(frameIndex);
    }
  }
}
customElements.define("video-scroll-player", VideoScrollPlayer);
